/**
 * Glass-styled popover for the learner experience.
 * Wraps React Aria Popover for positioning, focus management, and keyboard dismiss.
 */
import {
  Popover as AriaPopover,
  type PopoverProps as AriaPopoverProps,
} from 'react-aria-components';
import { useInterfacePortalContainer } from '@/foundation/ui/InterfacePortalContext';

export interface PopoverProps extends Omit<AriaPopoverProps, 'className' | 'style'> {
  className?: string;
}

const ANIM_IN = 'fl-popover-in 200ms cubic-bezier(0.16, 1, 0.3, 1) forwards';
const ANIM_OUT = 'fl-popover-out 150ms cubic-bezier(0.16, 1, 0.3, 1) forwards';

const KEYFRAMES = `
@keyframes fl-popover-in { from { opacity: 0; transform: translateY(-4px) scale(0.98) } to { opacity: 1; transform: translateY(0) scale(1) } }
@keyframes fl-popover-out { from { opacity: 1; transform: translateY(0) scale(1) } to { opacity: 0; transform: translateY(-4px) scale(0.98) } }
`;

export function Popover({ className, children, ...props }: PopoverProps) {
  const portalContainer = useInterfacePortalContainer();

  return (
    <>
      <style>{KEYFRAMES}</style>
      <AriaPopover
        UNSTABLE_portalContainer={portalContainer ?? undefined}
        {...props}
        className={className}
        style={({ isEntering, isExiting }) => ({
          borderRadius: 12,
          background: 'var(--color-surface)',
          backdropFilter: 'blur(24px) saturate(1.3)',
          WebkitBackdropFilter: 'blur(24px) saturate(1.3)',
          border: '1px solid var(--color-surface-border)',
          boxShadow:
            'inset 0 0.5px 0 var(--color-surface-highlight), 0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)',
          animation: isEntering ? ANIM_IN : isExiting ? ANIM_OUT : undefined,
          zIndex: 50,
          outline: 'none',
        })}
      >
        {children}
      </AriaPopover>
    </>
  );
}
